// All (/all): every task, project, page and list in one place, filtered by a
// small query language (lib/allQuery.ts) over what is already on the device.
// Tabs: the four kinds, then the queries pinned as tabs (Settings `all_tabs`).
// Open things unless the query asks for done ones; capped lists with Show more
// and no totals, anywhere.
import { useRef, useState } from 'react'
import { Link, useNavigate, useSearch } from '@tanstack/react-router'
import { Pin, X } from 'lucide-react'
import { db, mutate } from '@/runtime'
import {
  useAllEdges,
  useAllLists,
  useAllPages,
  useAllProjects,
  useAllTasks,
  useSettings,
  useWorkspaces,
} from '@/db/hooks'
import { SETTINGS_ID, type SettingsSpec } from '@/kinds/settings'
import { useScope } from '@/state/scope'
import { useNow } from '@/hooks/useNow'
import { dateOf } from '@/lib/time'
import { Icon } from '@/lib/icons'
import { PROJECT_STATE_WORD } from '@/lib/words'
import {
  FIELDS,
  FIELD_NAMES,
  applySuggestion,
  parseQuery,
  runQuery,
  suggest,
  type ItemKind,
  type Parsed,
  type Subject,
} from '@/lib/allQuery'
import {
  BUILT_IN_TABS,
  MAX_SAVED_TABS,
  MAX_TAB_NAME,
  MAX_TAB_QUERY,
  isBuiltIn,
  nameFor,
  pin,
  subjectsOf,
  tabsOf,
  type SavedTab,
} from '@/lib/allItems'
import { AppTabs } from '@/components/AppTabs'
import { TaskRow } from '@/components/TaskRow'
import { PageRow } from '@/kinds/page/components'
import { ListRow } from '@/kinds/list/components'
import { Button, Empty, PageTitle, cx } from '@/components/ui'

/** How many rows a group shows, and how many more each Show more adds. */
const PAGE = 20

const GROUPS: { kind: ItemKind; label: string }[] = [
  { kind: 'task', label: 'Tasks' },
  { kind: 'project', label: 'Projects' },
  { kind: 'page', label: 'Pages' },
  { kind: 'list', label: 'Lists' },
]

const HELP: [string, string][] = [
  ['state:next', 'or state:(next,waiting); done and dropped only when you ask'],
  ['project:alfieOS', 'ws:devos, label:home, kind:page'],
  ['priority>=2', 'due<7d, due:today, scheduled:none, updated>-3d'],
  ['free words', 'or "a phrase", looked for in names and notes'],
  ['a OR b', 'AND is the default; NOT x or -x leaves it out; ( ) group'],
  ['sort:due', 'priority, updated, created, name; sort:-name flips it'],
]

export const AllView = () => {
  const params = useSearch({ strict: false }) as { tab?: string; q?: string }
  const saved = tabsOf(useSettings().all_tabs)
  // a search handed over from Search has no kind: the page shows all four under its own tab
  const tab = params.tab ?? (params.q ? 'any' : 'task')
  const known = isBuiltIn(tab) || tab === 'any' || saved.some((t) => t.id === tab)
  const id = known ? tab : 'task'
  const start = saved.find((t) => t.id === id)?.q ?? params.q ?? ''
  return <AllInner key={id} tab={id} saved={saved} initial={start} />
}

const AllInner = ({ tab, saved, initial }: { tab: string; saved: SavedTab[]; initial: string }) => {
  const navigate = useNavigate()
  const scope = useScope((s) => s.ws)
  const now = useNow(60_000)
  const today = dateOf(new Date(now))
  const [q, setQ] = useState(initial)
  const [caret, setCaret] = useState(initial.length)
  const [focused, setFocused] = useState(false)
  // the last line that read: a half-typed `state:` keeps the page as it was, and says why
  const first = parseQuery(initial)
  const [good, setGood] = useState<Parsed>(first.ok ? first : { ok: true, node: { t: 'all' } })
  const [error, setError] = useState<string | null>(first.ok ? null : first.message)
  const [pinning, setPinning] = useState(false)
  const [name, setName] = useState('')
  const [shown, setShown] = useState<Partial<Record<ItemKind, number>>>({})
  const input = useRef<HTMLInputElement>(null)

  const tasks = useAllTasks()
  const projects = useAllProjects()
  const pages = useAllPages()
  const lists = useAllLists()
  const edges = useAllEdges()
  const suites = useWorkspaces()
  const subjects = subjectsOf({
    tasks: tasks ?? [],
    projects: projects ?? [],
    pages: pages ?? [],
    lists: lists ?? [],
    edges,
  })
  const byId = {
    task: new Map((tasks ?? []).map((r) => [r.id, r])),
    project: new Map((projects ?? []).map((r) => [r.id, r])),
    page: new Map((pages ?? []).map((r) => [r.id, r])),
    list: new Map((lists ?? []).map((r) => [r.id, r])),
  }
  const projectOf = new Map<string, string>()
  for (const e of edges) if (e.relation === 'belongs_to') projectOf.set(e.from_id, e.to_id)

  const kinds = isBuiltIn(tab) ? [tab] : undefined
  const result = runQuery(good, subjects, { today, scope, kinds })

  const ctx = {
    projects: [...new Set(subjects.filter((s) => s.kind === 'project' && s.open).map((s) => s.title))].sort(),
    suites: suites.map((w) => w.spec.slug).sort(),
    labels: [
      ...new Set(
        subjects.flatMap((s) =>
          Object.entries(s.labels).flatMap(([k, v]) => (k === 'ws' ? [] : [k, `${k}=${v}`])),
        ),
      ),
    ].sort(),
  }
  const chips = focused ? suggest(q, caret, ctx) : []

  const edit = (text: string, at: number) => {
    setQ(text)
    setCaret(at)
    setShown({})
    const p = parseQuery(text)
    if (p.ok) {
      setGood(p)
      setError(null)
    } else setError(p.message)
  }

  const current = saved.find((t) => t.id === tab)
  const changed = !!current && q.trim() !== current.q.trim()
  const canPin =
    q.trim().length > 0 && error === null && saved.length < MAX_SAVED_TABS && (!current || changed)

  const save = async (next: SavedTab[]) => {
    const patch = { all_tabs: next } as Partial<SettingsSpec>
    if (await db.resources.get(SETTINGS_ID)) await mutate.patch(SETTINGS_ID, { spec: patch })
    else await mutate.create('Settings', patch, {}, SETTINGS_ID)
  }
  const doPin = async () => {
    const next = pin(saved, tab, q, name)
    if (!next) return
    await save(next)
    setPinning(false)
    setName('')
    void navigate({ to: '/all', search: { tab: next[next.length - 1]!.id } as never, replace: true })
  }
  const update = async () => {
    if (!current || error) return
    await save(saved.map((t) => (t.id === current.id ? { ...t, q: q.trim().slice(0, MAX_TAB_QUERY) } : t)))
  }
  const unpin = async () => {
    if (!current) return
    await save(saved.filter((t) => t.id !== current.id))
    void navigate({ to: '/all', search: { tab: 'task' } as never, replace: true })
  }

  const tabs = [
    ...BUILT_IN_TABS.map((t) => ({ to: '/all', label: t.label, search: { tab: t.id }, on: tab === t.id })),
    ...saved.map((t) => ({ to: '/all', label: t.name, search: { tab: t.id }, on: tab === t.id })),
    ...(tab === 'any' ? [{ to: '/all', label: 'Search', search: { tab: 'any' }, on: true }] : []),
  ]

  const groups = GROUPS.filter((g) => result[g.kind].length > 0)
  const rows = (g: ItemKind, list: Subject[]) => {
    const n = shown[g] ?? PAGE
    const slice = list.slice(0, n)
    return (
      <>
        <ul className="group-list" data-testid={`all-${g}s`}>
          {slice.map((s) => {
            if (g === 'task') {
              const t = byId.task.get(s.id)
              if (!t) return null
              return (
                <TaskRow
                  key={s.id}
                  task={t}
                  project={byId.project.get(projectOf.get(t.id) ?? '')}
                  mode="compact"
                />
              )
            }
            if (g === 'page') {
              const p = byId.page.get(s.id)
              return p ? <PageRow key={s.id} resource={p} /> : null
            }
            if (g === 'list') {
              const l = byId.list.get(s.id)
              return l ? <ListRow key={s.id} resource={l} /> : null
            }
            const p = byId.project.get(s.id)
            if (!p) return null
            return (
              <li key={s.id}>
                <Link
                  to="/projects/$id"
                  params={{ id: p.id }}
                  className="flex items-center gap-2 px-3.5 py-2.5 min-h-12 hover:bg-raised/50"
                >
                  <Icon value={p.spec.icon} size={18} />
                  <span className="min-w-0 flex-1 truncate font-medium">{p.spec.title}</span>
                  {p.status.state !== 'active' && (
                    <span className="text-xs text-faint">{PROJECT_STATE_WORD[p.status.state]}</span>
                  )}
                </Link>
              </li>
            )
          })}
        </ul>
        {list.length > n && (
          <Button
            variant="quiet"
            className="mt-1"
            onClick={() => setShown((x) => ({ ...x, [g]: n + PAGE }))}
            data-testid={`all-more-${g}`}
          >
            Show more
          </Button>
        )}
      </>
    )
  }

  return (
    <div>
      <PageTitle>All</PageTitle>
      <AppTabs tabs={tabs} label="All" />
      <div className="relative mb-2">
        <input
          ref={input}
          value={q}
          onChange={(e) => edit(e.target.value, e.target.selectionStart ?? e.target.value.length)}
          onSelect={(e) => setCaret(e.currentTarget.selectionStart ?? 0)}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          placeholder="state:next project:alfieOS due<7d"
          className="w-full"
          type="text"
          inputMode="text"
          autoCapitalize="off"
          autoCorrect="off"
          autoComplete="off"
          spellCheck={false}
          enterKeyHint="done"
          aria-label="Query"
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? 'all-error' : undefined}
          data-testid="all-query"
          onKeyDown={(e) => {
            if (e.key === 'Escape') edit('', 0)
            if (e.key === 'Enter') input.current?.blur()
          }}
        />
      </div>
      {chips.length > 0 && (
        <div
          className="flex gap-1.5 overflow-x-auto scrollbar-none mb-2 -mx-1 px-1"
          role="listbox"
          aria-label="Suggestions"
          data-testid="all-suggest"
        >
          {chips.map((c) => (
            <button
              key={c.label + c.from}
              type="button"
              role="option"
              aria-selected={false}
              // keep the keyboard up: a tap on a chip must not take focus from the field
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                const a = applySuggestion(q, c)
                edit(a.text, a.caret)
                input.current?.focus()
                requestAnimationFrame(() => input.current?.setSelectionRange(a.caret, a.caret))
              }}
              className="shrink-0 rounded-full border border-line bg-raised px-3 min-h-8 text-[13px] text-fg-2 hover:text-fg"
            >
              {c.label}
              {FIELD_NAMES.includes(c.label as never) && c.insert.endsWith(':') && (
                <span className="text-faint ml-1">
                  {FIELDS[c.label as keyof typeof FIELDS].hint.split(',')[0]}
                </span>
              )}
            </button>
          ))}
        </div>
      )}
      {error && (
        <p id="all-error" role="alert" className="text-sm text-danger mb-3" data-testid="all-error">
          {error}
        </p>
      )}
      <div className="flex flex-wrap items-center gap-2 mb-4 min-h-8">
        {canPin && !pinning && (
          <Button
            variant="chip"
            onClick={() => {
              setName(nameFor(q))
              setPinning(true)
            }}
            data-testid="all-pin"
          >
            <Pin size={13} aria-hidden /> {current ? 'Pin as new tab' : 'Pin as tab'}
          </Button>
        )}
        {changed && !error && (
          <Button variant="chip" onClick={() => void update()} data-testid="all-update">
            Update this tab
          </Button>
        )}
        {current && (
          <Button variant="chip" onClick={() => void unpin()} data-testid="all-unpin">
            <X size={13} aria-hidden /> Unpin
          </Button>
        )}
      </div>
      {pinning && (
        <form
          className="flex items-center gap-2 mb-4"
          onSubmit={(e) => {
            e.preventDefault()
            void doPin()
          }}
        >
          <input
            value={name}
            onChange={(e) => setName(e.target.value.slice(0, MAX_TAB_NAME))}
            aria-label="Tab name"
            placeholder="Tab name"
            className="min-w-0 flex-1"
            autoFocus
            data-testid="all-pin-name"
          />
          <Button variant="primary" type="submit" data-testid="all-pin-save">
            Pin
          </Button>
          <Button variant="quiet" onClick={() => setPinning(false)}>
            Cancel
          </Button>
        </form>
      )}
      {groups.length === 0 && <Empty>{tasks && projects && pages && lists ? 'Nothing matches.' : ''}</Empty>}
      {groups.map((g) => (
        <section key={g.kind} className="mb-5">
          {(groups.length > 1 || !isBuiltIn(tab)) && (
            <h2 className="text-[13px] font-semibold text-muted mb-2">{g.label}</h2>
          )}
          {rows(g.kind, result[g.kind])}
        </section>
      ))}
      <details className={cx('mt-6 text-sm text-muted')}>
        <summary className="cursor-pointer min-h-8 flex items-center">How to ask</summary>
        <dl className="grid grid-cols-1 gap-y-2 mt-2">
          {HELP.map(([a, b]) => (
            <div key={a}>
              <dt className="text-fg-2 font-medium">{a}</dt>
              <dd className="text-faint">{b}</dd>
            </div>
          ))}
        </dl>
      </details>
    </div>
  )
}
